// src/components/CompletionJourney.js
// Three-act completion experience: Passport → Film Strip → Recap
import React, { useState, useRef } from 'react'
import { CITIES, DAYS } from '../data/content'
import { MapSVG, MAPS_LINES } from './TransitMap'
import shareImage from '../utils/shareImage'

const CITY_PHOTOS = {
  nyc:    ['nyc1','nyc2','nyc3','nyc4','nyc5','nyc6','nyc7'],
  paris:  ['paris1','paris2','paris3','paris4','paris5','paris6','paris7'],
  london: ['london1','london2','london3','london4','london5','london6','london7'],
  milan:  ['milan1','milan2','milan3','milan4','milan5','milan6','milan7'],
  berlin: ['berlin1','berlin2','berlin3','berlin4','berlin5','berlin6','berlin7'],
  madrid: ['madrid1','madrid2','madrid3','madrid4','madrid5','madrid6','madrid7'],
}
// Returns null for cities with no stock photo pool (currently just 'august' — there
// are no "magic" city photos to fall back to) rather than silently showing NYC's.
// Callers must handle a null return themselves — do not fall back to CITY_PHOTOS.nyc here.
const getPhoto = (city, n) => {
  const pool = CITY_PHOTOS[city]
  if (!pool) return null
  return 'https://magic.soappower.com/images/cities/' + pool[(n - 1) % pool.length] + '.jpg'
}

const COMPLETION_MSG = {
  nyc:    { title: 'New York', sub: 'You did the Magic.' },
  paris:  { title: 'Paris',   sub: 'Vous avez fait la Magie.' },
  london: { title: 'London',  sub: 'You did the Magic.' },
  milan:  { title: 'Milano',  sub: 'Hai fatto la Magia.' },
  berlin: { title: 'Berlin',  sub: 'Du hast die Magie gemacht.' },
  madrid: { title: 'Madrid',  sub: 'Hiciste la Magia.' },
  august: { title: 'Magic', sub: 'You did the Magic.' },
}

export default function CompletionJourney({ session, city, entries, onClose, onSharePassport }) {
  const [act, setAct] = useState(0) // 0=passport, 1=filmstrip, 2=recap
  const filmRef = useRef(null)
  const shareCardRef = useRef(null)
  const [sharing, setSharing] = useState(false)
  const cityData = CITIES[city] || {}
  const color = cityData.primaryColor || '#EE352E'
  const msg = COMPLETION_MSG[city] || COMPLETION_MSG.nyc
  const days = DAYS[city] || []
  const isAugust = city === 'august'
  // august's accent is gold — buttons using `color` as background need ink text, not white.
  const onAccent = isAugust ? '#1a0800' : '#fff'

  // Build stats from entries
  const allEntries = Object.values(entries)
  const totalGrats = allEntries.reduce((sum, e) => sum + (e?.gratitudes || []).filter(g => g?.trim()).length, 0)
  const totalMoments = allEntries.filter(e => e?.images?.[0]).length
  const bestGrat = allEntries
    .flatMap(e => (e?.gratitudes || []).filter(g => g?.trim()))
    .sort((a, b) => b.length - a.length)[0] || ''

  // Collage: up to 4 real moment photos, fill rest with city stock photos
  const momentPhotos = Object.values(entries)
    .filter(e => e?.images?.[0])
    .map(e => e.images[0])
    .slice(0, 4)
  const collagePhotos = [
    ...momentPhotos,
    ...Array.from({ length: Math.max(0, 4 - momentPhotos.length) }, (_, i) =>
      getPhoto(city, i + 1)
    )
  ].slice(0, 4)

  // Share card capture using html2canvas
  const handleShareImage = async () => {
    setSharing(true)
    try {
      const el = shareCardRef.current
      if (!el) throw new Error('no ref')
      const h2c = await new Promise(resolve => {
        if (window.html2canvas) { resolve(window.html2canvas); return }
        const s = document.createElement('script')
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js'
        s.onload = () => resolve(window.html2canvas)
        s.onerror = () => resolve(null)
        document.head.appendChild(s)
      })
      if (!h2c) throw new Error('html2canvas unavailable')
      const canvas = await h2c(el, {
        scale: 3, useCORS: true, allowTaint: true,
        backgroundColor: null, logging: false,
        width: el.offsetWidth, height: el.offsetHeight,
      })
      const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.93))
      const shareMsg = `✦ I just completed the ${msg.title} Line on The Magic Journey!\n\n${totalGrats} gratitudes written across 28 stations.\n\n${window.location.host}`
      await shareImage(blob, 'magic-journey-complete.jpg', shareMsg)
    } catch(e) {
      if (e.name !== 'AbortError') console.error('Share error:', e)
    }
    setSharing(false)
  }

  // All 28 postcards for film strip
  const postcards = Array.from({ length: 28 }, (_, i) => {
    const n = i + 1
    const entry = entries[n]
    const grat = entry?.gratitudes?.find(g => g?.trim()) || ''
    const photo = entry?.images?.[0] || getPhoto(city, n)
    return { n, grat, photo }
  })

  // Share passport text
  const shareText = `✦ I just completed the ${msg.title} Line on The Magic Journey!\n\n${totalGrats} gratitudes written across 28 stations.\n\n${window.location.host}`

  // No transit-line data exists for august — drop the Map act rather than show an empty one.
  const ACT_LABELS = isAugust ? ['Passport', 'Postcards', 'Recap'] : ['Passport', 'Postcards', 'Recap', 'Map']

  return (
    <div data-city={city} style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: 'var(--bg, #F5F0E8)',
      display: 'flex', flexDirection: 'column',
      fontFamily: 'Helvetica Neue, Arial, sans-serif',
      overflowY: act !== 1 ? 'auto' : 'hidden',
    }}>
      {/* Top bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 20px', borderBottom: `1px solid var(--border, rgba(0,0,0,0.08))`,
        flexShrink: 0,
      }}>
        {/* Act indicators */}
        <div style={{ display: 'flex', gap: 6 }}>
          {ACT_LABELS.map((l, i) => (
            <button key={i} onClick={() => setAct(i)} style={{
              padding: '4px 12px', borderRadius: 20,
              border: `1.5px solid ${act === i ? color : 'var(--border, rgba(0,0,0,0.12))'}`,
              background: act === i ? color : 'transparent',
              color: act === i ? '#fff' : 'var(--text3, #999)',
              fontSize: 10, fontWeight: 700, letterSpacing: 1,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>{l}</button>
          ))}
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', color: 'var(--text4, #aaa)',
          fontSize: 13, cursor: 'pointer', padding: '4px 8px', fontFamily: 'inherit',
        }}>✕ Close</button>
      </div>

      {/* ── ACT 0: PASSPORT ── */}
      {act === 0 && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 20px' }}>
          <div style={{ width: '100%', maxWidth: 360 }}>

            {/* Card */}
            <div style={{
              background: isAugust ? 'var(--card-bg)' : 'var(--card, #fff)',
              borderRadius: 24, overflow: 'hidden',
              boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
              border: '0.5px solid var(--border, rgba(0,0,0,0.08))',
            }}>
              <div style={{ height: 5, background: color }} />
              <div style={{ padding: '24px 22px 18px' }}>
                <div style={{ fontSize: 9, letterSpacing: 3, color: 'var(--text4)', fontWeight: 600 }}>THE MAGIC JOURNEY</div>
                <div style={{ fontSize: 28, fontWeight: 900, color: 'var(--text)', letterSpacing: -0.5, lineHeight: 1, marginTop: 4 }}>{msg.title}</div>
                <div style={{ fontSize: 10, letterSpacing: 2.5, color, fontWeight: 700, marginTop: 6 }}>LINE COMPLETE</div>
              </div>

              {/* Stamp */}
              <div style={{
                background: 'var(--bg)', padding: '28px 20px',
                borderTop: '0.5px solid var(--border)', borderBottom: '0.5px solid var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{
                  width: 130, height: 130, borderRadius: '50%',
                  border: `3px solid ${color}`,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                  position: 'relative',
                }}>
                  <div style={{ position: 'absolute', inset: 7, borderRadius: '50%', border: `1px dashed ${color}`, opacity: 0.3 }} />
                  <div style={{ fontSize: 10, letterSpacing: 3, color, fontWeight: 700 }}>{city.toUpperCase()}</div>
                  <div style={{ fontSize: 38, fontWeight: 900, color, lineHeight: 1 }}>28</div>
                  <div style={{ fontSize: 8, letterSpacing: 2, color, opacity: 0.7 }}>STATIONS</div>
                  <div style={{ fontSize: 9, color, opacity: 0.5, marginTop: 1 }}>
                    {new Date().toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }).toUpperCase()}
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
                {[
                  { n: totalGrats, l: 'Gratitudes' },
                  { n: 28, l: 'Days' },
                  { n: totalMoments, l: 'Moments' },
                ].map((s, i) => (
                  <div key={i} style={{
                    padding: '14px 8px', textAlign: 'center',
                    borderRight: i < 2 ? '0.5px solid var(--border)' : 'none',
                  }}>
                    <div style={{ fontSize: 22, fontWeight: 900, color, lineHeight: 1 }}>{s.n}</div>
                    <div style={{ fontSize: 8, letterSpacing: 1.5, color: 'var(--text4)', marginTop: 3 }}>{s.l.toUpperCase()}</div>
                  </div>
                ))}
              </div>

              <div style={{ padding: '12px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '0.5px solid var(--border)' }}>
                <div style={{ fontSize: 8, letterSpacing: 2, color: 'var(--text4)' }}>MAGIC JOURNEY · COMPLETED</div>
                <div style={{ color, fontSize: 13 }}>✦</div>
              </div>
            </div>

            {/* Buttons */}
            <button onClick={handleShareImage} style={{
              width: '100%', marginTop: 14, padding: 15,
              borderRadius: 14, border: 'none', background: color,
              color: onAccent, fontSize: 11, fontWeight: 800,
              letterSpacing: 2, textTransform: 'uppercase',
              cursor: 'pointer', fontFamily: 'inherit',
              boxShadow: `0 4px 20px ${color}44`,
            }}>{ sharing ? '⏳ Preparing…' : '✦  Share Passport'}</button>

            <button onClick={() => setAct(1)} style={{
              width: '100%', marginTop: 10, padding: 13,
              borderRadius: 14, border: '1.5px solid var(--border)',
              background: 'transparent', color: 'var(--text3)',
              fontSize: 11, fontWeight: 700, letterSpacing: 1,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>View your 28 postcards →</button>
          </div>
        </div>
      )}

      {/* ── ACT 1: FILM STRIP ── */}
      {act === 1 && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px 8px' }}>
            <div style={{ fontSize: 9, letterSpacing: 3, color: 'var(--text4)', fontWeight: 600 }}>YOUR 28 POSTCARDS</div>
            <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--text)', marginTop: 2 }}>Swipe through your journey</div>
          </div>
          <div ref={filmRef} style={{
            flex: 1, overflowX: 'auto', overflowY: 'hidden',
            display: 'flex', gap: 12, padding: '12px 20px 24px',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
          }}>
            {postcards.map(({ n, grat, photo }) => (
              <div key={n} style={{
                flexShrink: 0, width: 240,
                borderRadius: 18, overflow: 'hidden',
                scrollSnapAlign: 'start',
                border: '0.5px solid var(--border)',
                background: isAugust ? 'var(--card-bg)' : 'var(--card)',
                display: 'flex', flexDirection: 'column',
              }}>
                <div style={{ position: 'relative', aspectRatio: '3/4', background: '#111' }}>
                  <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={e => { e.target.style.display = 'none' }} />
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.8) 100%)' }} />
                  <div style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(8px)', borderRadius: 20, padding: '4px 10px 4px 8px', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
                    <span style={{ color: '#fff', fontSize: 9, fontWeight: 700, letterSpacing: 1.5 }}>{n}</span>
                  </div>
                  {grat && (
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '0 12px 14px' }}>
                      <p style={{ color: '#fff', fontSize: 12, fontWeight: 700, fontStyle: 'italic', lineHeight: 1.4, margin: 0, textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>
                        "{grat.length > 60 ? grat.slice(0, 60) + '…' : grat}"
                      </p>
                    </div>
                  )}
                </div>
                <div style={{ padding: '10px 12px', borderTop: '0.5px solid var(--border)' }}>
                  <div style={{ fontSize: 9, letterSpacing: 1.5, color, fontWeight: 700 }}>{days[n-1]?.title || `Station ${n}`}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ padding: '0 20px 24px' }}>
            <button onClick={() => setAct(2)} style={{
              width: '100%', padding: 14, borderRadius: 14, border: 'none',
              background: color, color: onAccent, fontSize: 11, fontWeight: 800,
              letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit',
            }}>View your recap →</button>
          </div>
        </div>
      )}

      {/* ── ACT 2: RECAP ── */}
      {act === 2 && (
        <div style={{ flex: 1, padding: '20px 20px 40px', display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 480, margin: '0 auto', width: '100%' }}>
          <div style={{ background: isAugust ? 'var(--card-bg)' : 'var(--card)', border: '0.5px solid var(--border)', borderRadius: 18, padding: 20, borderTop: `3px solid ${color}` }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--text)', lineHeight: 1.25 }}>
              28 stations.<br />{totalGrats} acts of gratitude.
            </div>
            <div style={{ fontSize: 12, color: 'var(--text4)', marginTop: 6 }}>{msg.title} Line · {msg.sub}</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { n: totalGrats, l: 'Gratitudes written' },
              { n: totalMoments, l: 'Moments captured' },
              { n: 28, l: 'Postcards earned' },
              { n: 28, l: 'Day streak' },
            ].map((s, i) => (
              <div key={i} style={{ background: isAugust ? 'var(--card-bg)' : 'var(--card)', border: '0.5px solid var(--border)', borderRadius: 14, padding: 14 }}>
                <div style={{ fontSize: 26, fontWeight: 900, color, lineHeight: 1 }}>{s.n}</div>
                <div style={{ fontSize: 9, letterSpacing: 1.5, color: 'var(--text4)', marginTop: 3 }}>{s.l.toUpperCase()}</div>
              </div>
            ))}
          </div>

          {bestGrat && (
            <div style={{ background: isAugust ? 'var(--card-bg)' : 'var(--card)', border: '0.5px solid var(--border)', borderLeft: `3px solid ${color}`, borderRadius: 14, padding: 18 }}>
              <div style={{ fontSize: 9, letterSpacing: 2.5, color: 'var(--text4)', fontWeight: 600, marginBottom: 8 }}>A MOMENT FROM YOUR JOURNEY</div>
              <div style={{ fontSize: 15, fontStyle: 'italic', fontWeight: 500, color: 'var(--text)', lineHeight: 1.5 }}>"{bestGrat}"</div>
            </div>
          )}

          <button onClick={handleShareImage} style={{
            width: '100%', padding: 14, borderRadius: 14, border: 'none',
            background: color, color: onAccent, fontSize: 11, fontWeight: 800,
            letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit',
            boxShadow: `0 4px 20px ${color}44`,
          }}>{ sharing ? '⏳ Preparing…' : '✦  Share Recap'}</button>

          <button onClick={onClose} style={{
            width: '100%', padding: 13, borderRadius: 14,
            border: '1.5px solid var(--border)', background: 'transparent',
            color: 'var(--text3)', fontSize: 11, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>Keep the Magic Alive →</button>
        </div>
      )}
      {/* ── ACT 3: MAP ── */}
      {act === 3 && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '20px 24px 40px' }}>
          <div style={{ fontSize: 10, letterSpacing: 3, color: 'var(--text4)', fontWeight: 700, marginBottom: 4 }}>
            TRANSIT MAP
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--text)', letterSpacing: -0.5, marginBottom: 20 }}>
            {COMPLETION_MSG[city]?.title || city}
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <div style={{ width: '100%', background: '#0d1117', borderRadius: 16, overflow: 'hidden', padding: 8 }}>
              <MapSVG cityKey={city} animKey={`passport-${city}`} large />
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 20 }}>
            {(MAPS_LINES[city] || []).map(line => (
              <div key={line.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 18, height: 3, borderRadius: 2, background: line.color }} />
                <span style={{ fontSize: 9, color: 'var(--text4)', letterSpacing: 1, fontWeight: 600 }}>{line.id}</span>
              </div>
            ))}
          </div>
        </div>
      )}

    {/* ── HIDDEN SHARE CARD — captured by html2canvas ── */}
      <div ref={shareCardRef} style={{
        position: 'fixed', left: '-9999px', top: 0,
        width: 390, height: 488,
        borderRadius: 20, overflow: 'hidden',
        fontFamily: 'Helvetica Neue, Arial, sans-serif',
        background: color,
      }}>
        {/* Photo collage background — adaptive by moment count */}
        {(() => {
          const n = momentPhotos.length
          // 0 moments: single city stock photo
          // 1 moment: full bleed
          // 2 moments: side by side
          // 3-4 moments: 2x2 grid
          if (n <= 1) {
            const photo = n === 1 ? momentPhotos[0] : getPhoto(city, 1)
            return (
              <img src={photo} alt="" crossOrigin="anonymous"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                onError={e => { e.target.style.background = color }}
              />
            )
          }
          if (n === 2) {
            return (
              <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {momentPhotos.map((photo, i) => (
                  <img key={i} src={photo} alt="" crossOrigin="anonymous"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={e => { e.target.style.background = color }}
                  />
                ))}
              </div>
            )
          }
          // 3-4: 2x2 grid, fill with stock if only 3
          const photos = [
            ...momentPhotos.slice(0, 4),
            ...Array.from({ length: Math.max(0, 4 - momentPhotos.length) }, (_, i) => getPhoto(city, i + 1))
          ].slice(0, 4)
          return (
            <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 2 }}>
              {photos.map((photo, i) => (
                <img key={i} src={photo} alt="" crossOrigin="anonymous"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  onError={e => { e.target.style.background = color }}
                />
              ))}
            </div>
          )
        })()}

        {/* Dark gradient overlay */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.92) 65%)' }} />

        {/* Stamp — top right */}
        <div style={{
          position: 'absolute', top: 16, right: 16,
          width: 56, height: 56, borderRadius: '50%',
          border: '2px solid rgba(255,255,255,0.5)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: '#fff', lineHeight: 1 }}>28</div>
          <div style={{ fontSize: 6, letterSpacing: 1.5, color: 'rgba(255,255,255,0.5)' }}>DONE</div>
        </div>

        {/* Content */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: 24 }}>
          <div style={{ fontSize: 9, letterSpacing: 3, color: 'rgba(255,255,255,0.45)', fontWeight: 600, marginBottom: 6 }}>THE MAGIC JOURNEY</div>
          <div style={{ fontSize: 38, fontWeight: 900, color: '#fff', letterSpacing: -1, lineHeight: 1 }}>{msg.title}</div>
          <div style={{ fontSize: 10, letterSpacing: 2.5, color: 'rgba(255,255,255,0.55)', fontWeight: 600, marginTop: 5 }}>LINE COMPLETE · {new Date().toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }).toUpperCase()}</div>

          {/* Stats row */}
          <div style={{ display: 'flex', gap: 20, marginTop: 16, paddingTop: 16, borderTop: '0.5px solid rgba(255,255,255,0.2)' }}>
            {[
              { n: totalGrats, l: 'GRATITUDES' },
              { n: 28, l: 'STATIONS' },
              { n: totalMoments, l: 'MOMENTS' },
            ].map((s, i) => (
              <div key={i}>
                <div style={{ fontSize: 24, fontWeight: 900, color: '#fff', lineHeight: 1 }}>{s.n}</div>
                <div style={{ fontSize: 7, letterSpacing: 1.5, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>{s.l}</div>
              </div>
            ))}
          </div>

          {/* Best quote */}
          {bestGrat && (
            <div style={{ fontSize: 12, fontStyle: 'italic', color: 'rgba(255,255,255,0.7)', marginTop: 14, lineHeight: 1.45 }}>
              "{bestGrat.length > 80 ? bestGrat.slice(0, 80) + '…' : bestGrat}"
            </div>
          )}

          <div style={{ fontSize: 8, letterSpacing: 2, color: 'rgba(255,255,255,0.3)', marginTop: 12 }}>{window.location.host} · ✦</div>
        </div>
      </div>
    </div>
  )
}
